import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton } from "@mui/material";
import { ArrowDownward as ArrowDownwardIcon } from "@mui/icons-material";
import { colors } from "../theme/colors";
import { ChatMessageArea, ChatMessageAreaRef } from "./CustomChatMessageArea";
import { ChatInput } from "./CustomChatInput";
import type {
  AIChatInterfaceProps,
  Message,
  FileAttachment,
} from "./AIChatTypes";

export const AIChatInterface: React.FC<AIChatInterfaceProps> = ({
  open,
  onClose: _onClose,
  isExpanded = false,
  primaryColor = colors.primary.main,
  backgroundColor = "#FFFFFF",
  propertyId: _propertyId,
  onSendMessage: externalOnSendMessage,
  onConfirmAction,
  onRejectAction,
  onCancelSend,
  messages: externalMessages,
  isSending = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  isLoadingInitial = false,
  loadError = null,
  onRetry,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const chatMessageAreaRef = useRef<ChatMessageAreaRef>(null);

  // 外部からメッセージが提供されている場合はそれを使用、なければローカルステート
  const messages = externalMessages || localMessages;

  // チャットの開閉アニメーション制御
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        setShouldRender(true);
        setIsClosing(false);
      });
    } else {
      requestAnimationFrame(() => {
        setIsClosing(true);
      });
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSendMessage = (content: string, files: FileAttachment[]) => {
    if (externalOnSendMessage) {
      // 外部ハンドラーが提供されている場合はそれを使用（Redux連携時）
      externalOnSendMessage(content, files);
    } else {
      // ローカルステートで管理（スタンドアロン使用時）
      const newMessage: Message = {
        id: Date.now().toString(),
        content,
        sender: "user",
        timestamp: new Date(),
        files: files.length > 0 ? files : undefined,
      };
      setLocalMessages([...localMessages, newMessage]);
    }
  };

  const handleScrollToBottom = () => {
    chatMessageAreaRef.current?.scrollToBottom();
  };

  if (!shouldRender) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: backgroundColor,
        display: "flex",
        flexDirection: "column",
        animation: isClosing ? "fadeOut 0.2s ease-out" : "fadeIn 0.3s ease-in",
        "@keyframes fadeIn": {
          "0%": {
            opacity: 0,
          },
          "100%": {
            opacity: 1,
          },
        },
        "@keyframes fadeOut": {
          "0%": {
            opacity: 1,
          },
          "100%": {
            opacity: 0,
          },
        },
      }}
    >
      {/* Messages */}
      <ChatMessageArea
        ref={chatMessageAreaRef}
        messages={messages}
        primaryColor={primaryColor}
        isSending={isSending}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
        isLoadingInitial={isLoadingInitial}
        loadError={loadError}
        onRetry={onRetry}
        isExpanded={isExpanded}
        onScrollPositionChange={setShowScrollToBottom}
        onConfirmAction={onConfirmAction}
        onRejectAction={onRejectAction}
      />

      {/* Input Area（フローティング=右下/全画面 の入力まわりの余白は左右均等に） */}
      <Box
        data-testid="chat-composer"
        sx={{
          position: "relative",
          flexShrink: 0,
          zIndex: 10,
          pt: 0,
          px: 1,
          pb: 1,
          backgroundColor: colors.background.default,
        }}
      >
        <Box
          data-testid="chat-composer-fade"
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: (theme) => theme.spacing(-3),
            right: 0,
            left: 0,
            height: (theme) => theme.spacing(3),
            pointerEvents: "none",
            background: `linear-gradient(to bottom, transparent, ${colors.background.default} 72%)`,
          }}
        />

        {/* Scroll to Bottom Button */}
        {showScrollToBottom && (
          <Box
            sx={{
              position: "absolute",
              top: (theme) => theme.spacing(-5),
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 2,
            }}
          >
            <IconButton
              onClick={handleScrollToBottom}
              sx={{
                width: 32,
                height: 32,
                minWidth: 32,
                backgroundColor: primaryColor,
                color: colors.text.inverse,
                borderRadius: "50%",
                boxShadow: `0px 2px 8px ${colors.shadow.medium}`,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: primaryColor,
                  opacity: 0.9,
                  transform: "scale(1.05)",
                },
              }}
              aria-label="一番下にスクロール"
            >
              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <ChatInput
            onSendMessage={handleSendMessage}
            onCancelSend={onCancelSend}
            primaryColor={primaryColor}
            isSending={isSending}
            isExpanded={isExpanded}
          />
        </Box>
      </Box>
    </Box>
  );
};
